// MUI Imports
import type { Theme } from '@mui/material';

const toggleButton: Theme['components'] = {
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: ({ ownerState }) => ({
        backgroundColor: 'var(--mui-palette-divider)',
        boxSizing: 'content-box',
        height: '28px',
        padding: '2px',
        borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
        gap: '2px',
        display: 'flex',
        ...(ownerState.size === 'small' && {
          borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
        }),
        ...(ownerState.size === 'large' && {
          height: '40px',
          padding: '4px',
          gap: '4px',
          borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
        }),
        // A vertical group stacks its options, so the single-row height would clip them; each option
        // takes the row height instead, which a horizontal group gives it by stretching.
        ...(ownerState.orientation === 'vertical' && {
          height: 'auto',
          '& .MuiToggleButton-root': {
            height: ownerState.size === 'large' ? '40px' : '28px',
          },
        }),
      }),
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme, ownerState }) => {
        return {
          // Apply contained button base styles
          boxShadow: 'none',
          borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
          margin: '0 !important',
          border: 'none !important',

          // Override MUI's automatic border radius resets with higher specificity
          '&.MuiToggleButtonGroup-firstButton, &.MuiToggleButtonGroup-middleButton, &.MuiToggleButtonGroup-lastButton':
            {
              borderRadius: 'var(--mui-shape-customBorderRadius-sm) !important',
            },

          // Apply contained button padding based on size
          ...(ownerState.size === 'small' && {
            padding: '4px 12px',
          }),
          ...(ownerState.size === 'medium' && {
            padding: theme.spacing(2, 4.5),
          }),
          ...(ownerState.size === 'large' && {
            padding: theme.spacing(2.5, 5),
          }),

          // Unselected state - apply outlined button styles
          '&:not(.Mui-selected):not(.Mui-disabled)': {
            backgroundColor: 'transparent',
            color: 'var(--mui-palette-text-secondary)',
          },

          // Hover state for unselected
          '&:not(.Mui-selected):not(.Mui-disabled):hover': {
            backgroundColor: 'var(--mui-palette-primary-hover)',
            color: 'var(--mui-palette-primary-main)',
            boxShadow: 'none',
          },

          // Selected state (default surface: paper). Override per call site when needed.
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: 'var(--mui-palette-background-paper)',
            color: 'var(--mui-palette-primary-main)',
            boxShadow: 'none',
          },

          // Focus visible state
          '&.Mui-focusVisible:not(:has(span.MuiTouchRipple-root))': {
            backgroundColor: 'var(--mui-palette-primary-hover)',
            boxShadow: 'none',
          },

          // Disabled state
          '&.Mui-disabled': {
            opacity: 0.45,
            color: 'var(--mui-palette-primary-main)',
            backgroundColor: 'var(--mui-palette-background-paper)',
          },
        };
      },
      sizeSmall: ({ theme }) => ({
        borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
        fontSize: theme.typography.button.fontSize,
        lineHeight: theme.typography.button.lineHeight,
        // Override MUI's automatic border radius resets
        '&.MuiToggleButtonGroup-firstButton, &.MuiToggleButtonGroup-middleButton, &.MuiToggleButtonGroup-lastButton':
          {
            borderRadius: 'var(--mui-shape-customBorderRadius-sm) !important',
          },
      }),
      sizeLarge: ({ theme }) => ({
        borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
        fontSize: theme.typography.button.fontSize,
        lineHeight: theme.typography.button.lineHeight,
        // Override MUI's automatic border radius resets
        '&.MuiToggleButtonGroup-firstButton, &.MuiToggleButtonGroup-middleButton, &.MuiToggleButtonGroup-lastButton':
          {
            borderRadius: 'var(--mui-shape-customBorderRadius-sm) !important',
          },
      }),
    },
  },
};

export { toggleButton };
